import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { environment } from '#environments';
import { Vendor, VendorOption, VendorProject, VendorUser } from '#models';

@Injectable({ providedIn: 'root' })
export class VendorService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/vendors`;
    private _lookupUrl = `${environment.apiBaseUrl}/v1/reference-data/vendors`;

    /** Cached active-vendor lookup for dropdowns; cleared whenever an admin changes a vendor. */
    private _options$: Observable<VendorOption[]> | null = null;

    /** Admin list. Pass `true` to include inactive vendors. */
    getVendors(includeInactive = false): Observable<Vendor[]> {
        return includeInactive
            ? this._http.get<Vendor[]>(this._base, { params: { includeInactive: 'true' } })
            : this._http.get<Vendor[]>(this._base);
    }

    /** Active vendors for dropdowns (read-only lookup, no admin permission needed); cached until a vendor changes. */
    getVendorOptions(): Observable<VendorOption[]> {
        this._options$ ??= this._http.get<VendorOption[]>(this._lookupUrl).pipe(
            map(options => options.filter(option => option.isActive)),
            shareReplay(1),
        );
        return this._options$;
    }

    getById(vendorId: string): Observable<Vendor> {
        return this._http.get<Vendor>(`${this._base}/${vendorId}`);
    }

    create(name: string): Observable<Vendor> {
        return this._http.post<Vendor>(this._base, { name }).pipe(tap(() => this._invalidateOptions()));
    }

    update(vendorId: string, name: string): Observable<Vendor> {
        return this._http.put<Vendor>(`${this._base}/${vendorId}`, { vendorId, name }).pipe(tap(() => this._invalidateOptions()));
    }

    deactivate(vendorId: string): Observable<void> {
        return this._http.patch<void>(`${this._base}/${vendorId}/deactivate`, {}).pipe(tap(() => this._invalidateOptions()));
    }

    delete(vendorId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${vendorId}`).pipe(tap(() => this._invalidateOptions()));
    }

    /** Returns the projects currently assigned to a vendor. */
    getProjects(vendorId: string): Observable<VendorProject[]> {
        return this._http.get<VendorProject[]>(`${this._base}/${vendorId}/projects`);
    }

    assignProject(vendorId: string, projectId: string): Observable<void> {
        return this._http.post<void>(`${this._base}/${vendorId}/projects/${projectId}`, {});
    }

    removeProject(vendorId: string, projectId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${vendorId}/projects/${projectId}`);
    }

    /** Returns the external Okta users associated with a vendor. */
    getUsers(vendorId: string): Observable<VendorUser[]> {
        return this._http.get<VendorUser[]>(`${this._base}/${vendorId}/users`);
    }

    /** Assigns an Okta external (third-party) user to a vendor. */
    assignUser(vendorId: string, userId: string): Observable<void> {
        return this._http.post<void>(`${this._base}/${vendorId}/users/${userId}`, {});
    }

    removeUser(vendorId: string, userId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${vendorId}/users/${userId}`);
    }

    private _invalidateOptions(): void {
        this._options$ = null;
    }
}